import { Dispatch, SetStateAction, useMemo } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { useTranslation } from 'react-i18next'

// local imports
import calendarr from 'assets/icons/calendar-gray.svg'
import time from 'assets/icons/time.svg'
import location from 'assets/icons/location-marker-gray.svg'
import resIcon from 'assets/icons/pool-vehicle-res.svg'
import vehicleIcon from 'assets/icons/pool-vehicle-gray.svg'
import building from 'assets/icons/bina-gray.svg'
import plate from 'assets/icons/list-branch.svg'
import DateUtil from 'utils/DateUtil'
import { IFilteredPoolVehicleList, IPoolVehicleTimeTableList } from 'types/PoolVehicleType'
import useSavePoolVehicleReservation from '../hooks/use-save-pool-vehicle-res'
import { InfoListItem, InfoList } from '../vehicles/styled'
import {
  ContentArea,
  Header,
  ImagePreview,
  ReservationSummary,
  SidebarActionArea,
  SidebarCancelButton,
  SidebarConfirmButton,
  SidebarContainer,
} from '../../gym/gym-list/styled'

interface ScheduleSidebarProps {
  openModal: () => void
  closeAfterSave: () => void
  visible: boolean
  setVisible: Dispatch<SetStateAction<boolean>>
  vehicleInfo: IFilteredPoolVehicleList
  reservationInfos: IPoolVehicleTimeTableList[]
}

const sortByStartTime = <T extends { reservationStartHourStr: string }>(arr: T[]): T[] => {
  return arr.sort((a, b) => {
    const timeA = new Date(`1970-01-01T${a.reservationStartHourStr}`)
    const timeB = new Date(`1970-01-01T${b.reservationStartHourStr}`)
    return timeA.getTime() - timeB.getTime()
  })
}

export default function ScheduleSidebar({
  vehicleInfo,
  reservationInfos,
  visible,
  setVisible,
  openModal,
  closeAfterSave,
}: ScheduleSidebarProps) {
  const { t } = useTranslation()
  const date = DateUtil.dateFormat(new Date(reservationInfos[0]?.reservationStartHour))
  const timeList = sortByStartTime(reservationInfos)
  const reservationTime = useMemo(
    () =>
      timeList?.map((r) => (
        <>
          {r.reservationStartHourStr === 'allDay' ? (
            t('pool-vehicle-res-modal.all-day')
          ) : (
            <>
              {r.reservationStartHourStr} - {r.reservationEndHourStr}
              <br />
            </>
          )}
        </>
      )),
    [t, timeList]
  )

  // Pool Vehicle information desctructuring
  const {
    locationToVehicle: { districtName, firmName, address },
  } = vehicleInfo || {}
  const vehicleLocationName = `${firmName}, ${districtName}, ${address || ''}`

  /* Handle Save Pool Vehicle reservation | Custom Hook */
  const { loading, savePoolVehicleReservation } = useSavePoolVehicleReservation(reservationInfos, closeAfterSave)

  const closeSidebar = () => {
    setVisible(false)
    openModal()
  }

  const reservationInfoItems = [
    {
      id: 'vehicle-brand',
      icon: vehicleIcon,
      title: t('pool-vehicle-res-modal.vehicle-brand'),
      value: vehicleInfo.brandOfVehicle,
    },
    {
      id: 'vehicle-model',
      icon: vehicleIcon,
      title: t('pool-vehicle-res-modal.vehicle-model'),
      value: vehicleInfo.modelOfVehicle,
    },
    {
      id: 'vehicle-plate',
      icon: plate,
      title: t('pool-vehicle-res-modal.license-plate'),
      value: vehicleInfo.plate,
    },
    {
      id: 'location-info',
      icon: location,
      title: t('lactation-res-modal.location'),
      value: vehicleLocationName,
    },
    {
      id: 'location-info-receive',
      icon: building,
      title: t('pool-vehicle-res-modal.location-to-receive'),
      value: vehicleInfo.locationToReceive,
    },
    {
      id: 'location-info-deliver',
      icon: building,
      title: t('pool-vehicle-res-modal.location-to-deliver'),
      value: vehicleInfo.locationToDeliver,
    },

    {
      id: 'date-info',
      icon: calendarr,
      title: t('lactation-res-modal.reservation-date'),
      value: date,
    },
    {
      id: 'time-info',
      icon: time,
      title: t('lactation-res-modal.reservation-time'),
      value: reservationTime,
    },
  ]

  return (
    <Sidebar
      style={{ width: '500px' }}
      className="of-bordered-sidebar-box"
      visible={visible}
      position="right"
      onHide={closeSidebar}
      dismissable={false}
      closeOnEscape={false}>
      <SidebarContainer>
        <ContentArea>
          <Header>{t('pool-vehicle-res-modal.pool-vehicle-reservation')}</Header>
          <ReservationSummary>{t('pool-vehicle-res-modal.reservation-summary')}</ReservationSummary>
          <ImagePreview url={resIcon} />

          <InfoList>
            <div className="reservation-subtitle">{t('pool-vehicle-res-modal.reservation-info')}</div>
            {reservationInfoItems.map((info) => (
              <InfoListItem key={info.id} hasList={info.id === 'time-info'}>
                {info.icon ? <img src={info.icon} alt={info.id} /> : <div style={{ width: 20, height: 20 }} />}
                <span className="reservation-info-title"> {info.title} : </span>
                <span className="reservation-info-value">{info.value}</span>
              </InfoListItem>
            ))}
          </InfoList>
        </ContentArea>

        {/* Action Buttons */}
        <SidebarActionArea>
          <SidebarCancelButton
            id="backToPoolVehicleReservation"
            className="p-button-outlined"
            label={t('coiffeur-res-modal.back-to-modal')}
            onClick={closeSidebar}
          />

          <SidebarConfirmButton
            id="savePoolVehicleReservation"
            loading={loading}
            label={t('buttons.confirm')}
            onClick={savePoolVehicleReservation}
          />
        </SidebarActionArea>
      </SidebarContainer>
    </Sidebar>
  )
}
